import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { SEOHead } from "@/components/SEOHead";
import { 
  Lock, 
  CheckCircle, 
  AlertCircle, 
  Eye, 
  EyeOff,
  ArrowLeft
} from "lucide-react";
import { toast } from "sonner";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { updatePassword } = useAuth();
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [validToken, setValidToken] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    password: '',
    confirmPassword: ''
  });

  // Extract token parameters from URL
  const accessToken = searchParams.get('access_token');
  const refreshToken = searchParams.get('refresh_token');
  const type = searchParams.get('type');

  useEffect(() => {
    const verifyResetToken = async () => {
      try {
        if (!accessToken || !refreshToken || type !== 'recovery') {
          setValidToken(false);
          setVerifying(false);
          return;
        }

        // Set the session with the tokens from the URL
        const { error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken
        });

        if (error) {
          console.error('Token verification error:', error);
          setValidToken(false);
        } else {
          setValidToken(true);
        }
      } catch (error) {
        console.error('Reset verification error:', error);
        setValidToken(false);
      } finally {
        setVerifying(false);
      }
    };

    verifyResetToken();
  }, [accessToken, refreshToken, type]);

  const handlePasswordReset = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { password, confirmPassword } = passwordData;

      // Validation
      if (!password || !confirmPassword) {
        toast.error("Please fill in both password fields");
        return;
      }

      if (password !== confirmPassword) {
        toast.error("Passwords do not match");
        return;
      }

      if (password.length < 6) {
        toast.error("Password must be at least 6 characters long");
        return;
      }

      console.log("Updating password...");

      const { error } = await updatePassword(password);

      if (error) {
        console.error("Password update error:", error);
        toast.error(error.message || "Failed to update password");
        return;
      }

      console.log("Password updated successfully");
      toast.success("Password updated successfully!");
      
      // Redirect to products page after successful reset
      setTimeout(() => {
        navigate("/products");
      }, 2000);

    } catch (err: any) {
      console.error("Unexpected password reset error:", err);
      toast.error("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <>
        <SEOHead
          title="Reset Password - BahaMarket"
          description="Complete your password reset for BahaMarket."
        />
        <div className="min-h-screen bg-background">
          <div className="container mx-auto px-4 py-8">
            <div className="max-w-md mx-auto">
              <Card>
                <CardContent className="p-8">
                  <div className="text-center space-y-4">
                    <LoadingSkeleton className="w-12 h-12 rounded-full mx-auto" />
                    <h2 className="text-lg font-medium">Verifying reset link...</h2>
                    <p className="text-sm text-muted-foreground">
                      Please wait while we verify your password reset request.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!validToken) {
    return (
      <>
        <SEOHead
          title="Invalid Reset Link - BahaMarket"
          description="The password reset link is invalid or has expired."
        />
        <div className="min-h-screen bg-background">
          <div className="container mx-auto px-4 py-8">
            <div className="max-w-md mx-auto">
              <Card>
                <CardHeader>
                  <div className="text-center">
                    <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                    <CardTitle>Invalid Reset Link</CardTitle>
                    <CardDescription>
                      This password reset link is invalid or has expired.
                    </CardDescription>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      Reset links expire after 24 hours for security reasons.
                    </AlertDescription>
                  </Alert>

                  <div className="space-y-3">
                    <Button 
                      onClick={() => navigate("/forgot-password")} 
                      className="w-full"
                    >
                      Request New Reset Link
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => navigate("/login")} 
                      className="w-full"
                    >
                      Back to Sign In
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEOHead
        title="Set New Password - BahaMarket"
        description="Set your new password for BahaMarket."
      />
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-md mx-auto">
            <div className="flex items-center gap-4 mb-6">
              <Button variant="ghost" onClick={() => navigate("/")}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Home
              </Button>
            </div>

            <Card>
              <CardHeader>
                <div className="text-center">
                  <Lock className="h-12 w-12 text-primary mx-auto mb-4" />
                  <CardTitle>Set New Password</CardTitle>
                  <CardDescription>
                    Choose a strong password for your account.
                  </CardDescription>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handlePasswordReset} className="space-y-4">
                  <div>
                    <Label htmlFor="password">New Password</Label>
                    <div className="relative mt-1">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        value={passwordData.password}
                        onChange={(e) => setPasswordData({ 
                          ...passwordData, 
                          password: e.target.value 
                        })}
                        required
                        minLength={6}
                        placeholder="Enter your new password"
                        disabled={loading}
                        className="pr-10"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={loading}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <Eye className="h-4 w-4 text-muted-foreground" />
                        )}
                      </Button>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="confirmPassword">Confirm New Password</Label>
                    <div className="relative mt-1">
                      <Input
                        id="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData({ 
                          ...passwordData, 
                          confirmPassword: e.target.value 
                        })}
                        required
                        minLength={6}
                        placeholder="Confirm your new password"
                        disabled={loading}
                        className="pr-10"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        disabled={loading}
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <Eye className="h-4 w-4 text-muted-foreground" />
                        )}
                      </Button>
                    </div>
                  </div>

                  {passwordData.password && passwordData.password.length < 6 && (
                    <Alert>
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        Password must be at least 6 characters long.
                      </AlertDescription>
                    </Alert>
                  )}

                  {passwordData.password && passwordData.confirmPassword && 
                   passwordData.password !== passwordData.confirmPassword && (
                    <Alert>
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        Passwords do not match.
                      </AlertDescription>
                    </Alert>
                  )}

                  <Button 
                    type="submit" 
                    disabled={loading || 
                      !passwordData.password || 
                      !passwordData.confirmPassword || 
                      passwordData.password !== passwordData.confirmPassword ||
                      passwordData.password.length < 6
                    } 
                    className="w-full"
                  >
                    {loading ? "Updating Password..." : "Update Password"}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <p className="text-sm text-muted-foreground">
                    After updating your password, you'll be signed in automatically.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default ResetPassword;